import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ServiceCard } from '../components/servis/ServiceCard';
import { ServisRequestForm } from '../components/servis/ServisRequestForm';
import { PartnerForm } from '../components/servis/PartnerForm';
import { servisers } from '../data/servisers';
import { useTranslation } from '../lib/i18n';
export function ServicePage() {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const requestFormRef = useRef<HTMLDivElement>(null);
  const [sectionsVisible, setSectionsVisible] = useState<Record<string, boolean>>({});
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target.id) {
          setSectionsVisible(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, {
      threshold: 0.15
    });
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const scrollToRequestForm = () => {
    requestFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const benefits = en ? ['Technical training', 'Access to spare parts', 'Manufacturer support', 'Inclusion in the official service network', 'Regional exclusivity (by agreement)'] : ru ? ['Техническое обучение', 'Доступ к запасным частям', 'Поддержка производителя', 'Включение в официальную сервисную сеть', 'Региональный эксклюзив (по согласованию)'] : ['Tehničku obuku', 'Pristup rezervnim delovima', 'Podršku proizvođača', 'Uvrštavanje u zvaničnu servisnu mrežu', 'Regionalnu ekskluzivu (po dogovoru)'];
  const requirements = en ? ['Registered shop or company', 'Experience working with electrical or industrial equipment', 'Field vehicle', 'Basic tools and diagnostic equipment', 'Readiness for field work'] : ru ? ['Зарегистрированный магазин или компания', 'Опыт работы с электрическим или промышленным оборудованием', 'Полевой автомобиль', 'Базовые инструменты и диагностическое оборудование', 'Готовность к полевым работам'] : ['Registrovana radnja ili firma', 'Iskustvo u radu sa elektro ili industrijskom opremom', 'Terensko vozilo', 'Osnovni alat i dijagnostička oprema', 'Spremnost za terenski rad'];
  return <main className="min-h-screen bg-dark-bg" data-id="element-971">
      {/* Hero */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-972">
        <div className="max-w-4xl mx-auto text-center" data-id="element-973">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4" data-id="element-974">
            {en ? 'Authorized service and ' : ru ? 'Авторизованный сервис и ' : 'Ovlašćeni servis i '}
            <span className="text-brand-yellow" data-id="element-975">
              {en ? 'technical support' : ru ? 'техническая поддержка' : 'tehnička podrška'}
            </span>
          </h1>
          <p className="text-lg text-gray-300" data-id="element-976">
            {en ? 'Expert service, available spare parts, and support across Serbia.' : ru ? 'Экспертный сервис, доступные запасные части и поддержка по всей Сербии.' : 'Stručan servis, dostupni rezervni delovi i podrška širom Srbije.'}
          </p>
        </div>
      </section>

      {/* Servisna mreža */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-977">
        <div className="max-w-6xl mx-auto" data-id="element-978">
          <h2 id="mreza" data-animate className={`text-3xl md:text-4xl font-black text-white mb-8 text-center transition-all duration-600 ${sectionsVisible.mreza ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-979">
            {en ? 'Service ' : ru ? 'Сервисная ' : 'Servisna '}
            <span className="text-brand-yellow" data-id="element-980">
              {en ? 'network' : ru ? 'сеть' : 'mreža'}
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-981">
            {servisers.map((serviser, index) => <ServiceCard key={serviser.id} serviser={serviser} index={index} data-id="element-982" />)}
          </div>
        </div>
      </section>

      {/* Online zahtev za servis */}
      <section ref={requestFormRef} className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border bg-dark-surface/50" data-id="element-983">
        <div className="max-w-3xl mx-auto" data-id="element-984">
          <h2 id="zahtev" data-animate className={`text-3xl md:text-4xl font-black text-white mb-4 text-center transition-all duration-600 ${sectionsVisible.zahtev ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-985">
            {en ? 'Online service ' : ru ? 'Онлайн-запрос на ' : 'Online zahtev za '}
            <span className="text-brand-yellow" data-id="element-986">
              {en ? 'request' : ru ? 'обслуживание' : 'servis'}
            </span>
          </h2>
          <p className="text-gray-300 text-center mb-10" data-id="element-987">
            {en ? 'Fill out the form below and our service team will contact you as soon as possible.' : ru ? 'Заполните форму ниже, и наша сервисная служба свяжется с вами в кратчайшие сроки.' : 'Popunite formu ispod i naša servisna služba će vas kontaktirati u najkraćem roku.'}
          </p>
          <div className="bg-dark-bg border border-dark-border rounded-xl p-6 md:p-8" data-id="element-988">
            <ServisRequestForm data-id="element-989" />
          </div>
        </div>
      </section>

      {/* Postani serviser */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-990">
        <div className="max-w-6xl mx-auto" data-id="element-991">
          <h2 id="partner" data-animate className={`text-3xl md:text-4xl font-black text-white mb-4 text-center transition-all duration-600 ${sectionsVisible.partner ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-992">
            {en ? 'Become a D-TECH ' : ru ? 'Станьте D-TECH ' : 'Postanite D-TECH '}
            <span className="text-brand-yellow" data-id="element-993">
              {en ? 'authorized service partner' : ru ? 'авторизованным сервисным партнером' : 'ovlašćeni serviser'}
            </span>
          </h2>
          <p className="text-gray-300 text-center mb-10 max-w-3xl mx-auto" data-id="element-994">
            {en ? 'We are expanding our service network and looking for technically qualified partners to work with D-TECH systems. We provide technical training, support, and availability of spare parts.' : ru ? 'Мы расширяем нашу сервисную сеть и ищем технически квалифицированных партнеров для работы с системами D-TECH. Мы предоставляем техническое обучение, поддержку и доступность запасных частей.' : 'Širimo servisnu mrežu i tražimo tehnički osposobljene partnere za rad sa D-TECH sistemima. Obezbeđujemo tehničku obuku, podršku i dostupnost rezervnih delova.'}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-995">
            {/* Left column */}
            <div data-id="element-996">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-997">
                {en ? 'What we ' : ru ? 'Что мы ' : 'Šta '}
                <span className="text-brand-yellow" data-id="element-998">
                  {en ? 'offer:' : ru ? 'предлагаем:' : 'nudimo:'}
                </span>
              </h3>
              <ul className="space-y-3" data-id="element-999">
                {benefits.map((benefit, index) => <li key={index} className="flex items-start gap-3 text-gray-300" data-id="element-1000">
                    <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-1001" />
                    <span data-id="element-1002">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            {/* Right column */}
            <div className="bg-dark-surface border border-dark-border rounded-xl p-6" data-id="element-1003">
              <h3 className="text-xl font-bold text-white mb-5" data-id="element-1004">
                {en ? 'Apply for service ' : ru ? 'Заявка на сервисное ' : 'Prijava za servisnu '}
                <span className="text-brand-yellow" data-id="element-1005">
                  {en ? 'partnership' : ru ? 'партнерство' : 'saradnju'}
                </span>
              </h3>
              <PartnerForm data-id="element-1006" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 bg-dark-surface border-t border-dark-border" data-id="element-1007">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1008">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" data-id="element-1009">
            {en ? 'Need an ' : ru ? 'Нужно ' : 'Potrebna vam je '}
            <span className="text-brand-yellow" data-id="element-1010">
              {en ? 'intervention?' : ru ? 'вмешательство?' : 'intervencija?'}
            </span>
          </h2>
          <p className="text-lg text-gray-300 mb-8" data-id="element-1011">
            {en ? 'Contact the responsible service technician or submit an online request.' : ru ? 'Свяжитесь с ответственным сервисным специалистом или отправьте онлайн-запрос.' : 'Kontaktirajte nadležnog servisera ili pošaljite zahtev online.'}
          </p>
          <Button variant="primary" size="lg" onClick={scrollToRequestForm} data-id="element-1012">
            {en ? 'Request service' : ru ? 'Запросить сервис' : 'Zatražite servis'}
          </Button>
        </div>
      </section>
    </main>;
}